import Image from '@/components/Image';
import {
  type HoverProductImagePriority,
  type ProductImagePriority,
  useProductImages,
} from '@/hooks/useProductImages';
import { EProductImageType } from '@/utils/enums/goods';
import Link from 'next/link';
import { forwardRef, useImperativeHandle, useState } from 'react';
import WapOnly from '../Responsive/WapOnly';
import WebOnly from '../Responsive/WebOnly';
import { Product } from './ProductCard';

// 定义暴露给父组件的接口
export interface ProductCardImgRef {
  getImages: () => {
    productImage: { src: string; type?: EProductImageType; isGray?: boolean };
    hoverImage: { src: string; type?: EProductImageType; isGray?: boolean };
  };
  refreshImages: () => void;
}

interface ProductCardImgProps {
  productData: Product;
  imageRule: EProductImageType[];
  imageHoverRule: EProductImageType[];
  productImagePriority?: ProductImagePriority;
  hoverProductImagePriority?: HoverProductImagePriority;
  isAcc: boolean;
  frameWidth: string;
  productImageWhiteBg?: boolean;
}

const ProductCardImg = forwardRef<ProductCardImgRef, ProductCardImgProps>(
  (
    {
      productData,
      imageRule,
      imageHoverRule,
      productImagePriority,
      hoverProductImagePriority,
      isAcc,
      frameWidth,
      productImageWhiteBg = false,
    },
    ref,
  ) => {
    const { productImage, hoverImage, refreshImages } = useProductImages({
      productData,
      imageRule,
      imageHoverRule,
      productImagePriority,
      hoverProductImagePriority,
    });
    const [loadedHoverImageSrc, setLoadedHoverImageSrc] = useState('');
    const isHoverImageReady = Boolean(hoverImage.src) && loadedHoverImageSrc === hoverImage.src;

    // 暴露给父组件的方法和数据
    useImperativeHandle(
      ref,
      () => ({
        getImages: () => ({
          productImage,
          hoverImage,
        }),
        refreshImages,
      }),
      [productImage, hoverImage, refreshImages],
    );
    return (
      <Link
        className="w-full h-full relative flex items-center justify-center group"
        href={`/goods-detail/${productData.shop_sku}`}
        aria-label={productData.name}
        onClick={(event: React.MouseEvent<HTMLAnchorElement>) => {
          event.preventDefault();
        }}
        // onNavigate={(event: React.MouseEvent<HTMLAnchorElement>) => {
        //   logger.warn('onNavigate===', event);
        //   event.preventDefault();
        // }}
        // style={{
        //   pointerEvents: 'none',
        // }}
      >
        {/* ！非镜框列表需要和镜框列表一样，hover上去展示一下素材图 */}
        {productData.is_glass === 1 ? (
          <>
            <WebOnly>
              <Image
                src={productImage.src}
                alt={productData.name}
                className={`object-cover ${isHoverImageReady ? 'group-hover:opacity-0' : ''} ${productImage.type === 1 ? 'h-auto  max-h-[67%]' : 'w-full h-full'} ${productData.is_glass === 1 && !productImage.isGray && productImage.type === 1 ? 'mix-blend-multiply' : ''} ${productData.status === 2 || productData.stock <= 0 ? 'opacity-50' : ''}`}
                style={{ width: productImage.type === 1 ? frameWidth : '100%' }}
                loading="lazy"
                width={hoverImage.type === 1 && productImage.type === 1 ? 348 : 397}
                height={hoverImage.type === 1 && productImage.type === 1 ? 184 : 298}
              />
              {hoverImage.src && (
                <div
                  className={`${productImageWhiteBg ? 'bg-white' : 'bg-auxiliary-light-gray'} absolute top-0 left-0 w-full h-full flex items-center justify-center pointer-events-none overflow-hidden flex-nowrap opacity-0 ${isHoverImageReady ? 'group-hover:opacity-100' : ''}`}
                >
                  <Image
                    key={hoverImage.src}
                    src={hoverImage.src}
                    alt={productData.name}
                    className={`object-cover ${hoverImage.type === 1 ? 'h-auto max-h-[67%]' : 'w-full h-full'} ${productData.is_glass === 1 && !hoverImage.isGray && hoverImage.type === 1 ? 'mix-blend-multiply' : ''} ${productData.status === 2 || productData.stock <= 0 ? 'opacity-50' : ''}`}
                    style={{ width: hoverImage.type === 1 ? frameWidth : '100%' }}
                    loading="lazy"
                    width={hoverImage.type === 1 ? 348 : 397}
                    height={hoverImage.type === 1 ? 184 : 298}
                    onLoad={() => setLoadedHoverImageSrc(hoverImage.src)}
                    onError={() => setLoadedHoverImageSrc('')}
                  />
                </div>
              )}
            </WebOnly>
            <WapOnly>
              <Image
                src={productImage.src}
                alt={productData.name}
                className={`object-cover ${productImage.type === 1 ? 'h-auto max-h-[67%]' : 'w-full h-full'} ${productData.is_glass === 1 && !productImage.isGray && productImage.type === 1 ? 'mix-blend-multiply' : ''} ${productData.status === 2 || productData.stock <= 0 ? 'opacity-50' : ''}`}
                style={{ width: productImage.type === 1 ? frameWidth : '100%' }}
                loading="lazy"
                width={hoverImage.type === 1 && productImage.type === 1 ? 148 : 186}
                height={hoverImage.type === 1 && productImage.type === 1 ? 74 : 223}
              />
            </WapOnly>
          </>
        ) : (
          // 暂时不直接和上面的逻辑进行合并，非镜框列表的展示逻辑还和镜框列表还不一样
          <>
            <Image
              src={productImage.src}
              alt={productData.name}
              className={`object-cover w-full h-full ${productData.is_glass === 1 && !productImage.isGray && productImage.type === 1 ? 'mix-blend-multiply' : ''} ${productData.status === 2 || productData.stock <= 0 ? 'opacity-50' : ''}`}
              loading="lazy"
              width={397}
              height={isAcc ? 298 : 397}
            />
            <WebOnly>
              {hoverImage.src && (
                <div
                  className={`${productImageWhiteBg ? 'bg-white' : 'bg-auxiliary-light-gray'} absolute top-0 left-0 w-full h-full flex items-center justify-center pointer-events-none overflow-hidden flex-nowrap opacity-0 ${isHoverImageReady ? 'group-hover:opacity-100' : ''} z-1`}
                >
                  <Image
                    key={hoverImage.src}
                    src={hoverImage.src}
                    alt={productData.name}
                    className={`object-cover ${hoverImage.type === 1 ? 'h-auto max-h-[67%]' : 'w-full h-full'} ${productData.is_glass === 1 && !hoverImage.isGray && hoverImage.type === 1 ? 'mix-blend-multiply' : ''} ${productData.status === 2 || productData.stock <= 0 ? 'opacity-50' : ''}`}
                    style={{ width: hoverImage.type === 1 ? frameWidth : '100%' }}
                    loading="lazy"
                    width={397}
                    height={isAcc ? 298 : 397}
                    onLoad={() => setLoadedHoverImageSrc(hoverImage.src)}
                    onError={() => setLoadedHoverImageSrc('')}
                  />
                </div>
              )}
            </WebOnly>
          </>
        )}
      </Link>
    );
  },
);

ProductCardImg.displayName = 'ProductCardImg';

export default ProductCardImg;
